import { type AddOnsSectionValue } from '@components/add-ons-section';
import {
  type AdvancedSettingsValue,
  ENTRANCE_TIME_SLOT_MODE_EVENT,
  ENTRANCE_TIME_SLOT_MODE_LIMITED,
} from '@components/advanced-settings-section';
import {
  type BasicInfoValue,
  type ExtraGuestsValue,
  SALES_END_TYPE_AT_EVENTS_END,
  SALES_END_TYPE_AT_EVENTS_START,
  SALES_END_TYPE_CUSTOM_DATE,
  SALES_START_TYPE_AFTER_OFFER,
  SALES_START_TYPE_CUSTOM_DATE,
  SALES_START_TYPE_NOW,
  type SalesEndType,
  type SalesStartType,
} from '@components/basic-info-section';
import {
  CHANNEL_QUOTA_PER_CHANNEL,
  CHANNEL_QUOTA_UNLIMITED,
  DISTRIBUTION_MODE_OFFLINE,
  DISTRIBUTION_MODE_ONLINE,
  type DistributionChannelLimitValue,
  type DistributionMode,
  type DistributionValue,
} from '@components/distribution-section';
import { type FormFieldsSectionValue } from '@components/form-fields-section';
import {
  type RegulationSettingsValue,
  SIAE_STATUS_APPROVED,
  SIAE_STATUS_EMPTY,
  SIAE_STATUS_EXCLUDED,
} from '@components/regulation-settings-section';
import { MULTIPLE_CHOICE_ADDON, SINGLE_CHOICE_ADDON } from '@constants/admissions';
import { type SupportedLanguages } from '@constants/constants.type';
import { DATE_FORMAT_SHORT_TIME } from '@constants/datetime';
import {
  type Admission,
  ADMISSION_STATUS_ON_SALE,
  ADMISSION_TYPE_BOTTLE_SERVICE,
  ADMISSION_TYPE_TICKET,
  type Translations,
} from '@domain/admission';
import { type ActiveChannelRequest } from '@domain/channel';
import { type Channel as EventTeamChannel, type EventTeam } from '@domain/eventTeam';
import { type OfferTeam } from '@hooks/admissions/type';
import { collectChannelIds, collectClosedChannelIds } from '@utils/channel-tree';
import { fromBackend } from '@utils/map-date/from-backend';
import { type Moment } from 'moment-timezone';

import { type AdmissionFlow, type AdmissionFormType, type AdmissionFormValue } from '../admission-form.type';
import { ADMISSION_FORM_FLOW_ADMISSION, ADMISSION_FORM_TYPE_PASS } from '../constants';

export interface FromBackendContext {
  flow: AdmissionFlow;
  type: AdmissionFormType;
  offer?: Admission | null;
  mainLanguage: SupportedLanguages;
  timezone: string;
  now: Moment;
  eventStartingTime: number;
  eventEndingTime: number;
  /**
   * Event-level `needsUserVerified` default. Seeds the offer toggle on create so the displayed
   * state matches what the BE would store when the field is omitted (it falls back to the event's
   * value for admissions).
   */
  eventNeedsUserVerified: boolean;
  isSuperAdmin: boolean;
  canExcludeSiaeOffers: boolean;
  hasItalyRegulationContext: boolean;
  isTicketsLeftThresholdEnabled: boolean;
  isExtraGuestsForBottleServiceEnabled: boolean;
}

const EMPTY_TRANSLATIONS: Translations = { ca: '', de: '', en: '', es: '', fr: '', it: '', pt: '' };

const emptyFormFieldGroup = () => ({ presetRequirements: {}, customFields: [] });

const toExtraGuestsValue = (maxQty: number | null, pricePerGuest: number | null): ExtraGuestsValue => ({
  enabled: maxQty !== null,
  maxQty: maxQty === null ? '' : maxQty.toString(),
  pricePerGuest: pricePerGuest === null ? '' : pricePerGuest.toString(),
});

const buildBottleServiceFields = (
  offer: Admission | null | undefined,
  isExtraGuestsEnabled: boolean
): Partial<BasicInfoValue> => {
  const bottleService = offer && 'guestCapacity' in offer ? offer : null;
  return {
    downpayment: offer && 'onlinePaymentPercentage' in offer ? offer.price.onlinePrice?.toString() ?? '' : '',
    guestsPerAdmission: bottleService ? bottleService.guestCapacity.toString() : '',
    // Gated behind a PostHog flag: omit the value entirely when off so the section doesn't render it
    // (absent value field ⇒ not rendered), and to-backend leaves the BE extra-guest fields untouched.
    ...(isExtraGuestsEnabled && {
      extraGuests: toExtraGuestsValue(
        bottleService?.extraGuestMaxQuantity ?? null,
        bottleService?.pricePerExtraGuest ?? null
      ),
    }),
  };
};

export const deriveSalesStartType = (offer: Admission | null | undefined): SalesStartType => {
  if (!offer) return SALES_START_TYPE_NOW;
  if (offer.offerReleasedBefore) return SALES_START_TYPE_AFTER_OFFER;
  return SALES_START_TYPE_CUSTOM_DATE;
};

export const deriveSalesEndType = (
  offer: Admission | null | undefined,
  eventStartingTime: number,
  eventEndingTime: number
): SalesEndType => {
  if (!offer || offer.salesTime.closingTime === eventStartingTime) return SALES_END_TYPE_AT_EVENTS_START;
  if (offer.salesTime.closingTime === eventEndingTime) return SALES_END_TYPE_AT_EVENTS_END;
  return SALES_END_TYPE_CUSTOM_DATE;
};

// DDP (diritto di prevendita) applies to tickets, bottle services and passes in Italy — not guest lists.
export const isDdpApplicable = (type: AdmissionFormType, hasItalyRegulationContext: boolean): boolean =>
  hasItalyRegulationContext &&
  (type === ADMISSION_TYPE_TICKET || type === ADMISSION_TYPE_BOTTLE_SERVICE || type === ADMISSION_FORM_TYPE_PASS);

const hasTranslatedContent = (offer: Admission | null | undefined, mainLanguage: SupportedLanguages): boolean =>
  [offer?.name, offer?.description].some(
    (block) =>
      block &&
      (Object.entries(block) as [SupportedLanguages, string | null][]).some(
        ([locale, text]) => locale !== mainLanguage && !!text
      )
  );

const buildBasicInfo = ({
  flow,
  type,
  offer,
  timezone,
  now,
  eventStartingTime,
  eventEndingTime,
  hasItalyRegulationContext,
  mainLanguage,
  isExtraGuestsForBottleServiceEnabled,
}: FromBackendContext): BasicInfoValue => {
  const isBottleService = type === ADMISSION_TYPE_BOTTLE_SERVICE;
  const isPass = type === ADMISSION_FORM_TYPE_PASS;
  const isAdmissionFlow = flow === ADMISSION_FORM_FLOW_ADMISSION;

  const value: BasicInfoValue = {
    price: offer?.price.amount.toString() ?? '',
    quantity: offer?.quantity == null ? '' : offer.quantity.toString(),
    itemsPerTransaction: {
      min: offer?.itemsPerTransaction.min.toString() ?? '',
      max: offer?.itemsPerTransaction.max.toString() ?? '',
    },
    title: offer?.name ?? { ...EMPTY_TRANSLATIONS },
    description: offer?.description ?? { ...EMPTY_TRANSLATIONS },
    translationsEnabled: hasTranslatedContent(offer, mainLanguage),
  };

  if (isDdpApplicable(type, hasItalyRegulationContext)) {
    value.ddp = offer?.settings.italyRegulation?.dirittoPrevendita?.toString() ?? '';
  }

  if (isBottleService) {
    Object.assign(value, buildBottleServiceFields(offer, isExtraGuestsForBottleServiceEnabled));
  }

  if (isPass) {
    value.passValidity = { start: null, end: null, days: [] };
  }

  if (isAdmissionFlow) {
    const start = offer?.salesTime.openingTime ? fromBackend(offer.salesTime.openingTime, timezone) : now;
    const end = fromBackend(offer ? offer.salesTime.closingTime : eventStartingTime, timezone);
    value.salesWindow = {
      startType: deriveSalesStartType(offer),
      startDate: start,
      startTime: start.format(DATE_FORMAT_SHORT_TIME),
      endType: deriveSalesEndType(offer, eventStartingTime, eventEndingTime),
      endDate: end,
      endTime: end.format(DATE_FORMAT_SHORT_TIME),
    };
  }

  return value;
};

export const deriveDistributionMode = (offer?: Admission | null): DistributionMode => {
  if (offer?.settings.distribution) return offer.settings.distribution;
  return offer?.settings.isPrivate ? DISTRIBUTION_MODE_OFFLINE : DISTRIBUTION_MODE_ONLINE;
};

const buildDistribution = (offer?: Admission | null): DistributionValue => ({
  mode: deriveDistributionMode(offer),
  accessCode: { enabled: !!offer?.accessCode, code: offer?.accessCode ?? '' },
});

/** Minimal offer shape `seedChannelLimit` reads — satisfied structurally by both `Admission` and `Pass`. */
export interface ChannelSeedSource {
  settings: { channelDefaultMaxQuantity: number | null; isForAllChannels: boolean };
  channels?: { id: string; maxQuantity: string | number | null }[];
}

export const seedChannelLimit = (
  eventTeam: EventTeam,
  offer?: ChannelSeedSource | null,
  offerTeams?: OfferTeam[],
  activeChannels?: ActiveChannelRequest
): DistributionChannelLimitValue => {
  const defaultMax = offer?.settings.channelDefaultMaxQuantity ?? null;
  const isForAllChannels = activeChannels?.isForAllChannels ?? offer?.settings.isForAllChannels ?? false;
  // Per-channel caps + selection come from the offer's active-channels (edit) or, when those are not
  // fetched (preset-seeded create), from the offer.channels seed carried by `buildAdmissionFromPreset`.
  const sourceChannels: { id: string; maxQuantity: string | number | null }[] =
    activeChannels?.channels ?? offer?.channels ?? [];
  const maxById = new Map(sourceChannels.map((channel) => [channel.id, channel.maxQuantity]));
  const teamMaxById = new Map((offerTeams ?? []).map((team) => [team.teamId, team.quantity]));

  const applyMax = (channel: EventTeamChannel): EventTeamChannel => {
    let raw: string | number | null | undefined = null;
    if (maxById.has(channel.channel.id)) {
      raw = maxById.get(channel.channel.id);
    } else if (isForAllChannels) {
      raw = defaultMax;
    }
    const maxTicketQuantity = raw === null || raw === undefined || raw === '' ? null : Number(raw);
    return { ...channel, maxTicketQuantity };
  };

  const channels: EventTeam = {
    teams: eventTeam.teams.map((team) => ({
      ...team,
      maxTicketQuantity: teamMaxById.has(team.id) ? teamMaxById.get(team.id) ?? null : team.maxTicketQuantity,
      channels: team.channels.map(applyMax),
    })),
    channelsWithoutTeams: eventTeam.channelsWithoutTeams.map(applyMax),
  };

  const closedIds = collectClosedChannelIds(eventTeam);
  const selectableIds = collectChannelIds(eventTeam).filter((id) => !closedIds.has(id));
  // A subset is conveyed by an explicit channel list (isForAllChannels:false); sold-everywhere ⇒ all.
  const hasExplicitChannels = sourceChannels.length > 0 && !isForAllChannels;
  const selectedChannelIds = hasExplicitChannels
    ? sourceChannels.map((channel) => channel.id).filter((id) => selectableIds.includes(id))
    : selectableIds;

  const hasPerChannelCap = sourceChannels.some((channel) => channel.maxQuantity != null);
  // Legacy parity: an offer re-opens as "per channel" whenever any cap exists (a saved "equal-for-all"
  // default seeds onto every channel via applyMax); only an uncapped offer re-opens as "unlimited".
  const quotaMode = hasPerChannelCap || defaultMax !== null ? CHANNEL_QUOTA_PER_CHANNEL : CHANNEL_QUOTA_UNLIMITED;

  const enabled = quotaMode !== CHANNEL_QUOTA_UNLIMITED || selectedChannelIds.length < selectableIds.length;

  return { enabled, quotaMode, limitAllChannels: '', selectedChannelIds, channels };
};

const buildAddOns = (offer?: Admission | null): AddOnsSectionValue => ({
  selectedAddOnIds: offer?.addOns?.map((addOn) => addOn.id) ?? [],
  hasMandatoryAddOns: offer?.hasMandatoryAddOns ?? false,
  addOnChoiceMode: offer?.addOnChoiceMode === SINGLE_CHOICE_ADDON ? SINGLE_CHOICE_ADDON : MULTIPLE_CHOICE_ADDON,
});

const buildAdvanced = ({
  offer,
  timezone,
  isTicketsLeftThresholdEnabled,
}: FromBackendContext): AdvancedSettingsValue => {
  const validFrom = offer?.checkInTime?.validFrom ? fromBackend(offer.checkInTime.validFrom, timezone) : null;
  const validUntil = offer?.checkInTime?.validUntil ? fromBackend(offer.checkInTime.validUntil, timezone) : null;

  return {
    entranceTimeSlot: {
      mode: validFrom !== null || validUntil !== null ? ENTRANCE_TIME_SLOT_MODE_LIMITED : ENTRANCE_TIME_SLOT_MODE_EVENT,
      validFrom,
      validFromTime: validFrom?.format(DATE_FORMAT_SHORT_TIME) ?? '',
      validUntil,
      validUntilTime: validUntil?.format(DATE_FORMAT_SHORT_TIME) ?? '',
    },
    drinks: { enabled: !!offer?.drinksIncluded, quantity: offer?.drinksIncluded?.toString() ?? '' },
    ...(isTicketsLeftThresholdEnabled && {
      lowStockThreshold: {
        enabled: offer?.ticketsLeftThreshold != null,
        quantity: offer?.ticketsLeftThreshold?.toString() ?? '',
      },
    }),
  };
};

const buildFormFields = (offer?: Admission | null): FormFieldsSectionValue => ({
  isNominative: offer?.settings.isNominative ?? false,
  mainHolder: emptyFormFieldGroup(),
  ticketHolder: emptyFormFieldGroup(),
});

const buildRegulation = ({
  offer,
  isSuperAdmin,
  canExcludeSiaeOffers,
  hasItalyRegulationContext,
  eventNeedsUserVerified,
}: FromBackendContext): RegulationSettingsValue => {
  if (!hasItalyRegulationContext) return {};

  const italyRegulation = offer?.settings.italyRegulation;
  const value: RegulationSettingsValue = {};

  if (isSuperAdmin) {
    value.allows18App = italyRegulation?.allows18App ?? false;
    value.needsUserVerified = italyRegulation?.needsUserVerified ?? eventNeedsUserVerified;
    if (italyRegulation?.isSiaeApproved) {
      value.siaeStatus = SIAE_STATUS_APPROVED;
    } else if (italyRegulation?.isSiaeExcluded) {
      value.siaeStatus = SIAE_STATUS_EXCLUDED;
    } else {
      value.siaeStatus = SIAE_STATUS_EMPTY;
    }
    value.abbonamentoId = italyRegulation?.abbonamentoId ?? null;
  }

  if (canExcludeSiaeOffers && !isSuperAdmin) {
    value.excludeFromC1 = italyRegulation?.isSiaeExcluded ?? false;
  }

  return value;
};

export const toAdmissionFormValue = (ctx: FromBackendContext): AdmissionFormValue => ({
  status: ctx.offer?.status ?? ADMISSION_STATUS_ON_SALE,
  basicInfo: buildBasicInfo(ctx),
  distribution: buildDistribution(ctx.offer),
  addOns: buildAddOns(ctx.offer),
  advanced: buildAdvanced(ctx),
  formFields: buildFormFields(ctx.offer),
  appearance: { color: ctx.offer?.color ?? '' },
  vatRate: { vatRate: ctx.offer?.vatRate ?? null },
  regulation: buildRegulation(ctx),
});
